import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import MetricCard from '../../components/MetricCard';
import accountApi from '../../services/accountApi';

function PlatformOverviewPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await accountApi.getPlatformOverview();
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <h1 className="product-page-title">Platform Overview</h1>
      <p className="product-page-subtitle">Global Bakeonator operations snapshot.</p>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {stats && (
        <>
          <div className="row g-3 mb-3">
            {[
              ['Partners', stats.partners ?? stats.tenants, '/platform/partners'],
              ['Active partners', stats.active_partners ?? stats.active_tenants, '/platform/partners'],
              ['Pending partners', stats.pending_partners, '/platform/partners'],
              ['Users', stats.users, '/platform/users'],
              ['Projects', stats.projects, null],
              ['Queued jobs', stats.queued_jobs, '/platform/jobs'],
              ['Running jobs', stats.running_jobs, '/platform/jobs'],
              ['Failed jobs', stats.failed_jobs, '/platform/jobs'],
              ['Compat rejects', stats.compatibility_rejections, null],
              ['Pending invites', stats.pending_invitations, null],
              ['Idle workers', stats.active_workers, '/platform/workers'],
              ['Busy workers', stats.busy_workers, '/platform/workers'],
              ['Stale workers', stats.stale_workers, '/platform/workers'],
              ['Workers total', stats.workers_total, '/platform/workers'],
            ].map(([label, value, href]) => (
              <div key={label} className="col-md-4 col-lg-3">
                <MetricCard label={label} value={value} href={href} />
              </div>
            ))}
          </div>

          {stats.next_actions?.length > 0 && (
            <div className="product-card mb-3">
              <h2 className="h6">Recommended actions</h2>
              <div className="d-flex flex-wrap gap-2">
                {stats.next_actions.map((a) => (
                  <Link key={a.href} to={a.href} className="btn btn-outline-primary btn-sm">
                    {a.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="row g-3">
            <div className="col-lg-6">
              <div className="product-card h-100">
                <h2 className="h6">Recent partners</h2>
                {(stats.recent_partners || []).length === 0 ? (
                  <p className="mb-0 text-muted">No partners yet.</p>
                ) : (
                  <ul className="mb-0 ps-3">
                    {stats.recent_partners.map((p) => (
                      <li key={p.id}>
                        <Link to={`/platform/partners/${p.id}`}>{p.name}</Link>
                        <span className="text-muted small"> · {p.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="product-card h-100">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h2 className="h6 mb-0">Recent platform audit</h2>
                  <Link to="/platform/audit" className="small">
                    View all
                  </Link>
                </div>
                {(stats.recent_audit || []).length === 0 ? (
                  <p className="mb-0 text-muted">No recent events.</p>
                ) : (
                  <ul className="mb-0 ps-3">
                    {stats.recent_audit.map((e) => (
                      <li key={e.id} className="small">
                        <span className="mono">{e.action}</span>
                        {e.actor_email ? ` · ${e.actor_email}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PlatformOverviewPage;
